import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
  FormLabel, RadioGroup, FormControlLabel, Radio,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  createHumanCapitalGoal,
  createHumanCapitalIssue,
  fetchJobs,
  fetchBusinessProcesses,
  fetchStrategicGoals,
  fetchCustomerValues,
  fetchFinancialGoals,
  fetchStrategicIssues,
  fetchHumanCapitalGoals,
  fetchHumanCapitalIssues,
  fetchInfoCapitalGoalsApp,
  fetchInfoCapitalGoalsIt,
  fetchInfoCapitalGoalsData,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmHumanCapitalGoalCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [kpiType, setKpiType] = useState("job"); // "job" | "businessProcess"
  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [jobs, setJobs] = useState([]);

  const [selectedBusinessProcessId, setSelectedBusinessProcessId] = useState("");
  const [businessProcesses, setBusinessProcesses] = useState([]);

  const [sourceIds, setSourceIds] = useState([]);
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [humanCapitalGoals, setHumanCapitalGoals] = useState([]);
  const [humanCapitalIssues, setHumanCapitalIssues] = useState([]);
  const [infoCapitalGoalsApp, setInfoCapitalGoalsApp] = useState([]);
  const [infoCapitalGoalsIt, setInfoCapitalGoalsIt] = useState([]);
  const [infoCapitalGoalsData, setInfoCapitalGoalsData] = useState([]);
  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());
  const [bpSelectedIndicatorNames, setBpSelectedIndicatorNames] = useState([]);

  const sourceIndicators = selectedJob?.indicators || [];

  // BP mode: indicators from selected business process
  const selectedBusinessProcess = businessProcesses.find((bp) => bp.businessProcessId === selectedBusinessProcessId) || null;
  const bpSourceIndicators = selectedBusinessProcess?.indicators || [];

  useEffect(() => {
    (async () => {
      try {
        const [jbs, bps, sgGoals, cvGoals, fgGoals, issues, icApp, icIt, icData, existingSfs, hcIssues] = await Promise.all([
          fetchJobs(businessDomainId),
          fetchBusinessProcesses(businessDomainId).catch(() => []),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchStrategicIssues(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsApp(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsIt(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsData(businessDomainId).catch(() => []),
          fetchHumanCapitalGoals(businessDomainId).catch(() => []),
          fetchHumanCapitalIssues(businessDomainId).catch(() => []),
        ]);
        setJobs(Array.isArray(jbs) ? jbs : []);
        setBusinessProcesses(Array.isArray(bps) ? bps : []);
        setStrategicGoals(Array.isArray(sgGoals) ? sgGoals : []);
        setCustomerValues(Array.isArray(cvGoals) ? cvGoals : []);
        setFinancialGoals(Array.isArray(fgGoals) ? fgGoals : []);
        setStrategicIssues(Array.isArray(issues) ? issues : []);
        setInfoCapitalGoalsApp(Array.isArray(icApp) ? icApp : []);
        setInfoCapitalGoalsIt(Array.isArray(icIt) ? icIt : []);
        setInfoCapitalGoalsData(Array.isArray(icData) ? icData : []);
        setHumanCapitalGoals(Array.isArray(existingSfs) ? existingSfs : []);
        setHumanCapitalIssues(Array.isArray(hcIssues) ? hcIssues : []);
        const used = new Set(
          (Array.isArray(existingSfs) ? existingSfs : [])
            .flatMap((sf) => sf.indicators || [])
            .map((ind) => ind.indicatorName)
            .filter(Boolean)
        );
        setUsedIndicatorNames(used);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleJobChange = (e) => {
    const job = jobs.find((j) => j.jobId === e.target.value);
    setSelectedJob(job || null);
    setSelectedNames([]);
  };

  const handleKpiTypeChange = (e) => {
    setKpiType(e.target.value);
    setSelectedJob(null);
    setSelectedNames([]);
    setSelectedBusinessProcessId("");
    setSourceIds([]);
    setBpSelectedIndicatorNames([]);
  };

  const handleBpSourceChange = (e) => {
    setSourceIds(e.target.value);
    setBpSelectedIndicatorNames([]);
  };

  const handleBpChange = (e) => {
    setSelectedBusinessProcessId(e.target.value);
    setBpSelectedIndicatorNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (kpiType === "job") {
      if (sourceIndicators.length > 0 && selectedNames.length === 0) {
        notify.error(t("financialGoal.indicatorRequired"));
        return;
      }
    }
    try {
      if (kpiType === "job") {
        const allGoals = [...strategicIssues];
        const sources = sourceIds
          .map((id) => {
            const goal = allGoals.find((s) => s.successFactorId === id);
            if (!goal) return null;
            return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
          })
          .filter(Boolean);
        const indicators = selectedNames
          .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        await createHumanCapitalGoal({
          successFactorName,
          successFactorDescription,
          jobId: selectedJob?.jobId || "",
          indicators,
          sources,
          businessDomainId,
          companyId,
        });
      } else {
        const allGoals = [...humanCapitalGoals, ...humanCapitalIssues];
        const bpSources = sourceIds
          .map((id) => {
            const goal = allGoals.find((s) => s.successFactorId === id);
            if (!goal) return null;
            return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
          })
          .filter(Boolean);
        const bpIndicators = bpSelectedIndicatorNames
          .map((name) => bpSourceIndicators.find((ind) => ind.indicatorName === name))
          .filter(Boolean);
        await createHumanCapitalIssue({
          successFactorName,
          successFactorDescription,
          businessProcessId: selectedBusinessProcessId || undefined,
          tasks: [],
          businessDomainId,
          sources: bpSources,
          indicators: bpIndicators,
        });
      }
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/strategy-map/human-capital-goals`, { replace: true });
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("humanCapitalGoal.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>

          {/* KPI種類選択 */}
          <Grid size={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t("humanCapitalGoal.title")}</FormLabel>
              <RadioGroup row value={kpiType} onChange={handleKpiTypeChange}>
                <FormControlLabel value="job" control={<Radio />} label="結果目標" />
                <FormControlLabel value="businessProcess" control={<Radio />} label="原因目標" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          {/* ビジネスプロセス選択（原因目標モードのみ） */}
          {kpiType === "businessProcess" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("businessProcess.title")}</InputLabel>
                <Select
                  value={selectedBusinessProcessId}
                  label={t("businessProcess.title")}
                  onChange={handleBpChange}
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {businessProcesses.map((bp) => (
                    <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                      {bp.businessProcessName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* ジョブ選択（ジョブKPIのみ） */}
          {kpiType === "job" && jobs.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("job.title")}</InputLabel>
                <Select
                  value={selectedJob?.jobId || ""}
                  label={t("job.title")}
                  onChange={handleJobChange}
                >
                  {jobs.map((j) => (
                    <MenuItem key={j.jobId} value={j.jobId}>
                      {j.jobName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}


          {/* ソース選択（結果目標モード：全種類のゴール） */}
          {kpiType === "job" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
                <Select
                  multiple
                  value={sourceIds}
                  onChange={(e) => setSourceIds(e.target.value)}
                  input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
                >
                  {strategicIssues.length > 0 && [
                    <MenuItem key="__si_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("strategicIssue.list.title")} ──
                    </MenuItem>,
                    ...strategicIssues.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* ソース選択（原因目標モード：人的資本目標のみ） */}
          {kpiType === "businessProcess" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
                <Select
                  multiple
                  value={sourceIds}
                  onChange={(e) => setSourceIds(e.target.value)}
                  input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
                >
                  {humanCapitalGoals.length > 0 && [
                    <MenuItem key="__hcg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("humanCapitalGoal.list.title")} ──
                    </MenuItem>,
                    ...humanCapitalGoals.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                  {humanCapitalIssues.length > 0 && [
                    <MenuItem key="__hci_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                      ── {t("humanCapitalIssue.list.title")} ──
                    </MenuItem>,
                    ...humanCapitalIssues.map((item) => (
                      <MenuItem key={item.successFactorId} value={item.successFactorId}>
                        {item.successFactorName}
                      </MenuItem>
                    )),
                  ]}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* 指標選択（結果目標モード） */}
          {kpiType === "job" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
                <Select
                  multiple
                  disabled={sourceIndicators.length === 0}
                  value={selectedNames}
                  onChange={(e) => setSelectedNames(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
                >
                  {sourceIndicators.map((ind) => (
                    <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                      {ind.indicatorName}（{ind.unit}）{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* 指標選択（原因目標モード） */}
          {kpiType === "businessProcess" && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
                <Select
                  multiple
                  disabled={bpSourceIndicators.length === 0}
                  value={bpSelectedIndicatorNames}
                  onChange={(e) => setBpSelectedIndicatorNames(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
                >
                  {bpSourceIndicators.map((ind) => (
                    <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                      {ind.indicatorName}（{ind.unit}）{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/human-capital-goals`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
